import {
  CheckCircle2,
  Receipt,
  Calculator,
  FileText,
  Download,
  Landmark,
} from "lucide-react";
import StatCard from "../components/StatCard";
import Badge from "../components/Badge";
import LoadingSkeleton from "../components/LoadingSkeleton";
import { useAuth } from "../context/AuthContext";
import { useQuery } from "../lib/useQuery";
import { getPayments } from "../lib/queries";

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatCurrency(amount) {
  if (amount == null || isNaN(Number(amount))) return "—";
  return Number(amount).toLocaleString("es-ES", {
    style: "currency",
    currency: "EUR",
  });
}

export default function Payments() {
  const { user } = useAuth();
  const {
    data: payments,
    loading,
  } = useQuery(() => getPayments(user.id), [user.id]);

  if (loading) return <LoadingSkeleton />;

  const financed = payments?.filter((p) => p.financed) ?? [];
  const regular = payments?.filter((p) => !p.financed) ?? [];

  const paid = regular.filter((p) => p.status === "paid");
  const totalRegular = regular.length;
  const paidAmount = paid.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const totalAmount = regular.reduce((s, p) => s + (Number(p.amount) || 0), 0);

  return (
    <div className="animate-fade-in space-y-6">
      {/* Financed payments */}
      {financed.map((p) => (
        <div
          key={p.id}
          className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-5"
        >
          <div className="flex items-center gap-2 mb-3">
            <Landmark size={18} className="text-blue-600" />
            <span className="font-semibold text-blue-900">
              Pago Financiado
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-blue-600 mb-0.5">Programa</p>
              <p className="text-sm font-medium text-gray-900">
                {p.concept || "Programa"}
              </p>
            </div>
            {p.amount != null && (
              <div>
                <p className="text-xs text-blue-600 mb-0.5">Importe total</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatCurrency(p.amount)}
                </p>
              </div>
            )}
            <div>
              <p className="text-xs text-blue-600 mb-0.5">Fecha</p>
              <p className="text-sm font-medium text-gray-900">
                {formatDate(p.date)}
              </p>
            </div>
          </div>
        </div>
      ))}

      {/* Stats for regular payments */}
      {totalRegular > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            icon={CheckCircle2}
            value={`${paid.length}/${totalRegular}`}
            label="Pagos Realizados"
            color="green"
          />
          <StatCard
            icon={Receipt}
            value={formatCurrency(paidAmount)}
            label="Pagado hasta ahora"
            color="blue"
          />
          <StatCard
            icon={Calculator}
            value={formatCurrency(totalAmount)}
            label="Total del Programa"
            color="amber"
          />
        </div>
      )}

      {/* Regular payments table */}
      {regular.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 md:px-6 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <FileText size={18} className="text-brand" />
              Historial de Pagos
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[540px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {["Factura", "Concepto", "Importe", "Fecha", "Estado", ""].map(
                    (h) => (
                      <th
                        key={h}
                        className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500"
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {regular.map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3.5 text-xs font-medium font-mono text-gray-600">
                      {p.invoice_id || "—"}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-700">
                      {p.concept}
                    </td>
                    <td className="px-4 py-3.5 text-sm font-semibold text-gray-900">
                      {formatCurrency(p.amount)}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-600">
                      {formatDate(p.date)}
                    </td>
                    <td className="px-4 py-3.5">
                      <Badge
                        variant={p.status === "paid" ? "success" : "gray"}
                      >
                        {p.status === "paid" ? "Pagado" : "Pendiente"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3.5">
                      {p.invoice_url && (
                        <a
                          href={p.invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:text-gray-900 hover:border-gray-300 transition-all inline-block"
                        >
                          <Download size={14} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state when no payments at all */}
      {financed.length === 0 && regular.length === 0 && (
        <div className="text-center py-16">
          <Receipt size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400">No hay pagos registrados</p>
        </div>
      )}
    </div>
  );
}
